import { apiFetch } from '../lib/api'
import { API_BASE } from '@/lib/config'

function memoryNodesCacheKey(params?: {
  search?: string
  kind?: NodeKind
  conversation_id?: string
  limit?: number
  offset?: number
}) {
  return `sine_memory_nodes_cache_v1:${JSON.stringify(params ?? {})}`
}

function memoryGraphCacheKey(params?: {
  conversation_id?: string
  node_id?: string
  depth?: number
}) {
  return `sine_memory_graph_cache_v1:${JSON.stringify(params ?? {})}`
}

// ── Types matching memory_graph.py backend ────────────────────────────────────

export type NodeKind =
  | 'person'
  | 'preference'
  | 'project'
  | 'company'
  | 'goal'
  | 'fact'
  | 'idea'
  | 'workflow'
  | 'skill'
  | 'note'

// Legacy alias kept for backward compat with any UI components still using NodeType
export type NodeType = NodeKind

export type MemorySource = 'auto' | 'manual' | 'agent' | 'chat' | 'studio' | 'user' | 'import' | 'legacy_migration'
export type MemoryOrigin = 'agent' | 'chat' | 'studio' | 'user' | 'import' | 'legacy_migration'
export type MemoryStatus = 'active' | 'merged' | 'archived' | 'deleted'

export interface MemoryTag {
  id: string
  user_id: string
  node_id: string
  tag: string
  created_at: string
}

/** Matches the memory_nodes table and memory_graph.py response shape */
export interface MemoryNode {
  id: string
  user_id: string
  conversation_id?: string
  title: string
  /** Canonical field name used by backend */
  kind: NodeKind
  summary?: string
  content_markdown: string
  content_plain?: string
  confidence?: number
  importance: number
  origin: MemoryOrigin
  status: MemoryStatus
  visibility?: 'private' | 'shared' | 'public'
  created_at: string
  updated_at: string
  last_accessed_at?: string
  access_count?: number
  decay_score?: number
  parent_node_id?: string | null
  is_pinned?: boolean
  pin_reason?: string | null
  brain_label?: string | null
  tags?: string[]
  outbound?: MemoryEdge[]
  inbound?: MemoryEdge[]
  cluster_id?: string
  cluster_label?: string
  cluster_color?: string
  component_size?: number
  degree?: number
  neighbor_count?: number
  connected_node_ids?: string[]
  degree_score?: number
  importance_score?: number
  confidence_score?: number
  recency_score?: number
  activity_score?: number
  centrality_score?: number
  brain_tier?: 'core' | 'active' | 'peripheral'
  is_core?: boolean
}

/** Matches memory_edges table */
export interface MemoryEdge {
  id: string
  user_id: string
  from_node_id: string
  to_node_id: string
  relationship_type: string
  weight: number
  confidence?: number
  origin: string
  created_at: string
}

export interface MemoryGraph {
  nodes: MemoryNode[]
  edges: MemoryEdge[]
  meta?: {
    total_nodes: number
    total_edges: number
    cluster_count: number
    density: number
    core_node_ids: string[]
    contradiction_count?: number
    clusters: Array<{
      id: string
      label: string
      count: number
      color: string
    }>
  }
}

export interface MemoryExplain {
  node_id: string
  brain_tier: 'core' | 'active' | 'peripheral'
  brain_label?: string | null
  cluster: {
    id?: string
    label?: string
    color?: string
  }
  scores: {
    centrality: number
    importance: number
    confidence: number
    recency: number
    activity: number
    connections: number
  }
  reasons: Array<{
    label: string
    value: string
  }>
  contradictions: Array<{
    id: string
    title: string
    summary?: string
    brain_label?: string | null
    cluster_label?: string
    kind?: string
    confidence: number
    reason: string
  }>
  related_nodes: Array<{
    id: string
    title: string
    cluster_label?: string
    centrality_score: number
  }>
}

export interface MemoryTimelineEvent {
  type: string
  timestamp: string
  label: string
  description: string
  conversation_id?: string
  node_id?: string
  from_node_id?: string
  to_node_id?: string
}

/** Input for creating a new node — matches CreateNodeRequest in memory_graph.py */
export interface CreateMemoryNodeInput {
  title: string
  kind?: NodeKind
  summary?: string
  content_markdown?: string
  confidence?: number
  importance?: number
  conversation_id?: string
  tags?: string[]
  origin?: MemoryOrigin
}

/** Input for patching a node — matches PatchNodeRequest in memory_graph.py */
export interface UpdateMemoryNodeInput {
  title?: string
  kind?: NodeKind
  summary?: string
  content_markdown?: string
  content_plain?: string
  importance?: number
  tags?: string[]
  confidence?: number
  is_pinned?: boolean
  pin_reason?: string | null
  brain_label?: string | null
}

export interface ExtractMemoryInput {
  text: string
  conversation_id?: string
  language?: 'no' | 'en'
}

export interface MemorySuggestion {
  title: string
  content_markdown: string
  kind: NodeKind
  confidence: number
  source_text?: string
}

export interface MemoryEmbeddingBackfillResult {
  ok: boolean
  scanned: number
  embedded: number
  failed: number
  dry_run: boolean
  node_ids: string[]
  reason?: string
}

export interface MemoryDuplicateSuggestion {
  source: Pick<MemoryNode, 'id' | 'title' | 'kind' | 'summary' | 'updated_at'>
  target: Pick<MemoryNode, 'id' | 'title' | 'kind' | 'summary' | 'updated_at'>
  score: number
  reason: string
}

// ── API ───────────────────────────────────────────────────────────────────────

export const memoryApi = {
  getCachedNodes(params?: {
    search?: string
    kind?: NodeKind
    conversation_id?: string
    limit?: number
    offset?: number
  }): { nodes: MemoryNode[]; total: number } | null {
    try {
      const raw = localStorage.getItem(memoryNodesCacheKey(params))
      if (!raw) return null
      const parsed = JSON.parse(raw) as { nodes?: MemoryNode[]; total?: number }
      return {
        nodes: Array.isArray(parsed.nodes) ? parsed.nodes : [],
        total: typeof parsed.total === 'number' ? parsed.total : 0,
      }
    } catch {
      return null
    }
  },

  setCachedNodes(
    params: {
      search?: string
      kind?: NodeKind
      conversation_id?: string
      limit?: number
      offset?: number
    } | undefined,
    value: { nodes: MemoryNode[]; total: number },
  ): void {
    try {
      localStorage.setItem(memoryNodesCacheKey(params), JSON.stringify(value))
    } catch {
      // ignore cache failures
    }
  },

  getCachedGraph(params?: {
    conversation_id?: string
    node_id?: string
    depth?: number
  }): MemoryGraph | null {
    try {
      const raw = localStorage.getItem(memoryGraphCacheKey(params))
      if (!raw) return null
      return JSON.parse(raw) as MemoryGraph
    } catch {
      return null
    }
  },

  setCachedGraph(
    params: {
      conversation_id?: string
      node_id?: string
      depth?: number
    } | undefined,
    value: MemoryGraph,
  ): void {
    try {
      localStorage.setItem(memoryGraphCacheKey(params), JSON.stringify(value))
    } catch {
      // ignore cache failures
    }
  },
  // ── Nodes ──────────────────────────────────────────────────────────────────

  async listNodes(params?: {
    search?: string
    kind?: NodeKind
    conversation_id?: string
    limit?: number
    offset?: number
  }): Promise<{ nodes: MemoryNode[]; total: number }> {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.kind) qs.set('kind', params.kind)
    if (params?.conversation_id) qs.set('conversation_id', params.conversation_id)
    if (params?.limit) qs.set('limit', String(params.limit))
    if (params?.offset) qs.set('offset', String(params.offset))
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes?${qs}`)
    if (!res.ok) throw new Error('Kunne ikke hente minnenotater')
    const data = await res.json()
    const payload = {
      nodes: Array.isArray(data.nodes) ? data.nodes : (Array.isArray(data.items) ? data.items : []),
      total: typeof data.total === 'number' ? data.total : 0,
    }
    this.setCachedNodes(params, payload)
    return payload
  },

  async getNode(id: string): Promise<MemoryNode> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${id}`)
    if (!res.ok) throw new Error('Kunne ikke hente minnenotat')
    return res.json()
  },

  async createNode(input: CreateMemoryNodeInput): Promise<MemoryNode> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!res.ok) throw new Error('Kunne ikke opprette minnenotat')
    return res.json()
  },

  async updateNode(id: string, input: UpdateMemoryNodeInput): Promise<MemoryNode> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!res.ok) throw new Error('Kunne ikke oppdatere minnenotat')
    return res.json()
  },

  async deleteNode(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Kunne ikke slette minnenotat')
  },

  // ── Graph ──────────────────────────────────────────────────────────────────

  async getGraph(params?: {
    conversation_id?: string
    node_id?: string
    depth?: number
  }): Promise<MemoryGraph> {
    const qs = new URLSearchParams()
    if (params?.conversation_id) qs.set('conversation_id', params.conversation_id)
    if (params?.node_id) qs.set('node_id', params.node_id)
    if (params?.depth) qs.set('depth', String(params.depth))
    const res = await apiFetch(`${API_BASE}/api/memory-graph/graph?${qs}`)
    if (!res.ok) throw new Error('Kunne ikke hente minnegraf')
    const graph = await res.json()
    this.setCachedGraph(params, graph)
    return graph
  },

  // ── Edges ──────────────────────────────────────────────────────────────────

  async createEdge(
    from_node_id: string,
    to_node_id: string,
    relationship_type: string,
    weight?: number,
    confidence?: number,
  ): Promise<MemoryEdge> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/edges`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ from_node_id, to_node_id, relationship_type, weight, confidence }),
    })
    if (!res.ok) throw new Error('Kunne ikke opprette relasjon')
    return res.json()
  },

  async deleteEdge(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/edges/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Kunne ikke slette relasjon')
  },

  // ── Tags ──────────────────────────────────────────────────────────────────

  async listTags(): Promise<MemoryTag[]> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/tags`)
    if (!res.ok) throw new Error('Kunne ikke hente tagger')
    const data = await res.json()
    return data.tags || []
  },

  async createTag(tag: string): Promise<MemoryTag> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag }),
    })
    if (!res.ok) throw new Error('Kunne ikke opprette tag')
    return res.json()
  },

  // ── Retrieve ──────────────────────────────────────────────────────────────

  async retrieveRelevant(params: {
    query: string
    conversation_id?: string
    max_nodes?: number
    include_neighbors?: boolean
  }): Promise<MemoryNode[]> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/retrieve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Kunne ikke hente relevante minner')
    const data = await res.json()
    return data.memories || data.nodes || []
  },

  // ── Extract / Suggest ──────────────────────────────────────────────────────

  async backfillEmbeddings(limit = 50, dry_run = false): Promise<MemoryEmbeddingBackfillResult> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/embeddings/backfill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit, dry_run }),
    })
    if (!res.ok) throw new Error('Kunne ikke backfille minne-embeddings')
    return res.json()
  },

  async extractSuggestions(input: ExtractMemoryInput): Promise<MemorySuggestion[]> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!res.ok) throw new Error('Kunne ikke ekstrahere minneforslag')
    const data = await res.json()
    return data.candidates || []
  },

  async saveSuggestion(
    suggestion: MemorySuggestion,
    conversation_id?: string,
  ): Promise<MemoryNode> {
    return memoryApi.createNode({
      title: suggestion.title,
      content_markdown: suggestion.content_markdown,
      kind: suggestion.kind,
      confidence: suggestion.confidence,
      conversation_id,
      origin: 'chat',
    })
  },

  async saveSuggestedBatch(params: {
    candidates: MemorySuggestion[]
    conversation_id?: string
    source_run_id?: string
    origin?: MemoryOrigin
  }): Promise<{ saved: number }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/save-suggested`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Kunne ikke lagre minneforslag')
    return res.json()
  },

  // ── Merge ──────────────────────────────────────────────────────────────────

  async mergeNodes(source_id: string, target_id: string): Promise<{ ok: boolean; merged_into: string }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/merge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_id, target_id }),
    })
    if (!res.ok) throw new Error('Kunne ikke slå sammen minnenotater')
    return res.json()
  },

  // ── Export ────────────────────────────────────────────────────────────────

  async getDuplicateSuggestions(limit = 6): Promise<{ suggestions: MemoryDuplicateSuggestion[]; total: number }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/duplicate-suggestions?limit=${limit}`)
    if (!res.ok) throw new Error('Kunne ikke hente duplikatforslag')
    return res.json()
  },

  async exportMarkdown(node_id: string): Promise<{ markdown: string; filename: string }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${node_id}/markdown`)
    if (!res.ok) throw new Error('Kunne ikke eksportere minnenotat')
    return res.json()
  },
  // ── Decay & Dedup ─────────────────────────────────────────────────────────
  async runDecay(): Promise<{ updated: number }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/run-decay`, { method: 'POST' })
    if (!res.ok) throw new Error('Decay-kjøring feilet')
    return res.json()
  },
  async runDedup(): Promise<{ merged: number }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/run-dedup`, { method: 'POST' })
    if (!res.ok) throw new Error('Dedup-kjøring feilet')
    return res.json()
  },
  // ── Proactive suggest ─────────────────────────────────────────────────────
  async proactiveSuggest(conversation_text: string, conversation_id?: string): Promise<MemorySuggestion[]> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/proactive-suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_text, conversation_id }),
    })
    if (!res.ok) throw new Error('Proaktiv forslag feilet')
    const data = await res.json()
    return data.suggestions ?? []
  },
  // ── Shared / team memory ──────────────────────────────────────────────────
  async listSharedNodes(): Promise<MemoryNode[]> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/shared`)
    if (!res.ok) throw new Error('Kunne ikke hente delt minne')
    const data = await res.json()
    return data.nodes ?? []
  },
  async updateVisibility(node_id: string, visibility: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${node_id}/visibility`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visibility }),
    })
    if (!res.ok) throw new Error('Kunne ikke oppdatere synlighet')
  },
  // ── Provenance ────────────────────────────────────────────────────────────
  async getProvenance(node_id: string): Promise<{
    node_id: string
    title: string
    created_at: string
    origin: string
    conversation: { id: string; title: string; created_at: string } | null
    source_message_id: string | null
  }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${node_id}/provenance`)
    if (!res.ok) throw new Error('Kunne ikke hente proveniens')
    return res.json()
  },

  async pinNode(node_id: string, pinned: boolean, reason?: string): Promise<MemoryNode> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${node_id}/pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned, reason }),
    })
    if (!res.ok) throw new Error('Kunne ikke oppdatere kjerneminne')
    return res.json()
  },

  async explainNode(node_id: string): Promise<MemoryExplain> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${node_id}/explain`)
    if (!res.ok) throw new Error('Kunne ikke hente minneforklaring')
    return res.json()
  },

  async getTimeline(node_id: string): Promise<{ node_id: string; events: MemoryTimelineEvent[] }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/nodes/${node_id}/timeline`)
    if (!res.ok) throw new Error('Kunne ikke hente minnetidslinje')
    return res.json()
  },

  async getGraphTimeline(limit = 20): Promise<{
    events: MemoryTimelineEvent[]
    summary: {
      core_count: number
      cluster_count: number
      contradiction_count: number
    }
  }> {
    const res = await apiFetch(`${API_BASE}/api/memory-graph/timeline?limit=${limit}`)
    if (!res.ok) throw new Error('Kunne ikke hente hjernetidslinje')
    return res.json()
  },

}
